import { colorPaletteZero, designSystemColorPalettes, SharedUiColorNameV2, type AnyCssColor } from '@/shared/ui/theming';
import { IconName, UiIconComponent } from '@/ui/icon/ui-icon';
import type { LiteralValues } from '@/utils/typing/literal-values.type';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    ElementRef,
    inject,
    input,
    model,
    Renderer2,
    RendererStyleFlags2
} from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { AdvizeoUiElementSize } from '../../interfaces/AdvizeoUi.interface';

const sizeClassSuffix = '-button-size';
const importanceSuffix = '-button-style';

export const ButtonStyle = {
    FILL: 'fill',
    OUTLINE: 'outline',
    SUBTLE: 'subtle'
} as const;
export type ButtonStyle = LiteralValues<typeof ButtonStyle>;

@UntilDestroy()
@Component({
    styleUrls: ['./ui-button.scss'],
    selector: 'button[ui-button]',
    templateUrl: 'ui-button.html',
    imports: [UiIconComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '[class.is-active]': 'isActive()'
    }
})
export class UiButtonComponent {
    private _element = inject(ElementRef);
    private _renderer = inject(Renderer2);

    color = input.required<SharedUiColorNameV2>();
    iconName = input<IconName>();
    disabled = model(false);
    pending = input(false);
    buttonStyle = input<ButtonStyle>(ButtonStyle.FILL);
    size = input<Extract<AdvizeoUiElementSize, 'tiny' | 'small' | 'medium'>>(AdvizeoUiElementSize.MEDIUM);
    type = input('');
    isActive = model(false);

    highContrast = computed(() => this.color() === SharedUiColorNameV2.DEFAULT);
    colorPalette = computed(() => designSystemColorPalettes[this.color()]);
    isButtonDisabled = computed(() => this.disabled() || this.pending());
    displayedIconName = computed(() => (this.pending() ? IconName.LOADING : this.iconName()));
    iconColor = computed<AnyCssColor>(() => {
        switch (this.buttonStyle()) {
            case ButtonStyle.FILL:
                return colorPaletteZero;
            case ButtonStyle.SUBTLE:
            case ButtonStyle.OUTLINE:
                if (this.disabled()) {
                    return this.colorPalette()[8].solid;
                } else {
                    if (this.highContrast()) {
                        return this.colorPalette()[12].solid;
                    } else {
                        return this.colorPalette()[11].solid;
                    }
                }
        }
    });

    constructor() {
        effect(() => {
            if (this.isButtonDisabled()) {
                this._renderer.setAttribute(this._element.nativeElement, 'disabled', '');
            } else {
                this._renderer.removeAttribute(this._element.nativeElement, 'disabled');
            }
        });

        effect(() => {
            this.setSizeClassToHost(this.size());
        });

        effect(() => {
            this.setStyleClassToHost(this.buttonStyle());
        });

        effect(() => {
            for (const [variant, color] of Object.entries(this.colorPalette())) {
                this._renderer.setStyle(
                    this._element.nativeElement,
                    `--palette-${variant}-solid`,
                    color.solid,
                    RendererStyleFlags2.DashCase
                );
            }
        });
    }

    private setSizeClassToHost(size: AdvizeoUiElementSize) {
        Object.values(AdvizeoUiElementSize).forEach(s => {
            this._renderer.removeClass(this._element.nativeElement, s + sizeClassSuffix);
        });
        this._renderer.addClass(this._element.nativeElement, size + sizeClassSuffix);
    }

    private setStyleClassToHost(importance: ButtonStyle) {
        Object.values(ButtonStyle).forEach(i => {
            this._renderer.removeClass(this._element.nativeElement, i + importanceSuffix);
        });
        this._renderer.addClass(this._element.nativeElement, importance + importanceSuffix);
    }
}
